import React, { useState, useEffect } from "react";
import { Loader2, Save } from "lucide-react";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";

const inputCls = "w-full rounded-lg border border-ink/15 px-3 py-2 text-sm outline-none focus:border-coral";
const labelCls = "text-xs uppercase tracking-widest text-ink/40 font-semibold";

export default function PricingEditor() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/pricing").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) {
    return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-coral" /></div>;
  }

  const updateArrayItem = (key, idx, field, value) => {
    setData((d) => {
      const arr = [...d[key]];
      arr[idx] = { ...arr[idx], [field]: value };
      return { ...d, [key]: arr };
    });
  };

  const updateBundle = (field, value) => setData((d) => ({ ...d, bundle: { ...d.bundle, [field]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/pricing", data);
      toast.success("Prezzi aggiornati");
    } catch (e) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const Card = ({ children }) => (
    <div className="bg-white rounded-2xl p-5 space-y-3 shadow-[0_2px_12px_rgba(0,0,0,0.03)]">{children}</div>
  );

  return (
    <div data-testid="admin-pricing">
      <div className="flex justify-between items-center mb-5">
        <div>
          <h2 className="font-display font-bold text-2xl text-ink">Prezzi pagina "Prezzi e Soluzioni"</h2>
          <p className="text-ink/50 text-sm">Modifica importi e testi della pagina /prezzi.</p>
        </div>
        <button onClick={save} disabled={saving} data-testid="pricing-save" className="inline-flex items-center gap-2 bg-coral text-white rounded-full px-6 py-2.5 font-semibold hover:bg-[#e04047] transition-colors disabled:opacity-60">
          {saving ? <Loader2 size={18} className="animate-spin" /> : <><Save size={16} /> Salva</>}
        </button>
      </div>

      {/* SETUP */}
      <h3 className="font-display font-bold text-lg text-ink mb-3">1 · Creazione sito (una tantum)</h3>
      <div className="grid md:grid-cols-3 gap-4 mb-8">
        {data.setup.map((m, i) => (
          <Card key={i}>
            <div><span className={labelCls}>Etichetta</span><input className={inputCls} value={m.tier} onChange={(e) => updateArrayItem("setup", i, "tier", e.target.value)} data-testid={`setup-tier-${i}`} /></div>
            <div><span className={labelCls}>Titolo</span><input className={inputCls} value={m.title} onChange={(e) => updateArrayItem("setup", i, "title", e.target.value)} data-testid={`setup-title-${i}`} /></div>
            <div><span className={labelCls}>Prezzo (€)</span><input className={inputCls} value={m.price} onChange={(e) => updateArrayItem("setup", i, "price", e.target.value)} data-testid={`setup-price-${i}`} /></div>
            <div><span className={labelCls}>Descrizione</span><textarea rows={2} className={inputCls} value={m.desc} onChange={(e) => updateArrayItem("setup", i, "desc", e.target.value)} /></div>
            <div><span className={labelCls}>Caratteristiche (una per riga)</span><textarea rows={4} className={inputCls} value={(m.feats || []).join("\n")} onChange={(e) => updateArrayItem("setup", i, "feats", e.target.value.split("\n").map((x) => x.trim()).filter(Boolean))} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><span className={labelCls}>Testo bottone</span><input className={inputCls} value={m.cta} onChange={(e) => updateArrayItem("setup", i, "cta", e.target.value)} /></div>
              <div><span className={labelCls}>Badge</span><input className={inputCls} value={m.badge || ""} onChange={(e) => updateArrayItem("setup", i, "badge", e.target.value)} /></div>
            </div>
            <label className="flex items-center gap-2 text-sm text-ink"><input type="checkbox" checked={!!m.hero} onChange={(e) => updateArrayItem("setup", i, "hero", e.target.checked)} /> In evidenza</label>
          </Card>
        ))}
      </div>

      {/* EXTRAS */}
      <h3 className="font-display font-bold text-lg text-ink mb-3">Servizi extra</h3>
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        {data.extras.map((e, i) => (
          <Card key={i}>
            <div><span className={labelCls}>Titolo</span><input className={inputCls} value={e.title} onChange={(ev) => updateArrayItem("extras", i, "title", ev.target.value)} data-testid={`extra-title-${i}`} /></div>
            <div><span className={labelCls}>Prezzo (€)</span><input className={inputCls} value={e.price} onChange={(ev) => updateArrayItem("extras", i, "price", ev.target.value)} data-testid={`extra-price-${i}`} /></div>
            <div><span className={labelCls}>Descrizione</span><textarea rows={2} className={inputCls} value={e.desc} onChange={(ev) => updateArrayItem("extras", i, "desc", ev.target.value)} /></div>
          </Card>
        ))}
      </div>

      {/* BUNDLE */}
      <h3 className="font-display font-bold text-lg text-ink mb-3">Pacchetto bundle</h3>
      <Card>
        <div className="grid md:grid-cols-2 gap-3">
          <div><span className={labelCls}>Nome</span><input className={inputCls} value={data.bundle.name || ""} onChange={(e) => updateBundle("name", e.target.value)} data-testid="bundle-name" /></div>
          <div><span className={labelCls}>Prezzo (€)</span><input className={inputCls} value={data.bundle.price || ""} onChange={(e) => updateBundle("price", e.target.value)} data-testid="bundle-price" /></div>
          <div><span className={labelCls}>Prezzo barrato</span><input className={inputCls} value={data.bundle.old || ""} onChange={(e) => updateBundle("old", e.target.value)} /></div>
          <div><span className={labelCls}>Testo bottone</span><input className={inputCls} value={data.bundle.cta || ""} onChange={(e) => updateBundle("cta", e.target.value)} /></div>
        </div>
        <div><span className={labelCls}>Descrizione</span><textarea rows={2} className={inputCls} value={data.bundle.desc || ""} onChange={(e) => updateBundle("desc", e.target.value)} /></div>
      </Card>

      {/* SUBSCRIPTIONS */}
      <h3 className="font-display font-bold text-lg text-ink mt-8 mb-3">2 · Abbonamenti (mensile / annuale)</h3>
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        {data.subscriptions.map((s, i) => (
          <Card key={i}>
            <div><span className={labelCls}>Nome piano</span><input className={inputCls} value={s.tier} onChange={(e) => updateArrayItem("subscriptions", i, "tier", e.target.value)} data-testid={`sub-tier-${i}`} /></div>
            <div><span className={labelCls}>Titolo</span><input className={inputCls} value={s.title} onChange={(e) => updateArrayItem("subscriptions", i, "title", e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><span className={labelCls}>€ / mese</span><input className={inputCls} value={s.m} onChange={(e) => updateArrayItem("subscriptions", i, "m", e.target.value)} data-testid={`sub-m-${i}`} /></div>
              <div><span className={labelCls}>€ / anno</span><input className={inputCls} value={s.y} onChange={(e) => updateArrayItem("subscriptions", i, "y", e.target.value)} data-testid={`sub-y-${i}`} /></div>
            </div>
            <div><span className={labelCls}>Descrizione</span><textarea rows={2} className={inputCls} value={s.desc} onChange={(e) => updateArrayItem("subscriptions", i, "desc", e.target.value)} /></div>
            <div><span className={labelCls}>Caratteristiche (una per riga)</span><textarea rows={4} className={inputCls} value={(s.feats || []).join("\n")} onChange={(e) => updateArrayItem("subscriptions", i, "feats", e.target.value.split("\n").map((x) => x.trim()).filter(Boolean))} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><span className={labelCls}>Testo bottone</span><input className={inputCls} value={s.cta} onChange={(e) => updateArrayItem("subscriptions", i, "cta", e.target.value)} /></div>
              <div><span className={labelCls}>Badge</span><input className={inputCls} value={s.badge || ""} onChange={(e) => updateArrayItem("subscriptions", i, "badge", e.target.value)} /></div>
            </div>
            <label className="flex items-center gap-2 text-sm text-ink"><input type="checkbox" checked={!!s.hero} onChange={(e) => updateArrayItem("subscriptions", i, "hero", e.target.checked)} /> In evidenza</label>
          </Card>
        ))}
      </div>

      {/* ADDONS */}
      <h3 className="font-display font-bold text-lg text-ink mb-3">Add-on ricorrenti</h3>
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        {(data.addons || []).map((a, i) => (
          <Card key={i}>
            <div className="grid grid-cols-2 gap-2">
              <div><span className={labelCls}>Nome</span><input className={inputCls} value={a.label} onChange={(e) => updateArrayItem("addons", i, "label", e.target.value)} /></div>
              <div><span className={labelCls}>Prezzo / nota</span><input className={inputCls} value={a.value} onChange={(e) => updateArrayItem("addons", i, "value", e.target.value)} /></div>
            </div>
            <label className="flex items-center gap-2 text-sm text-ink"><input type="checkbox" checked={!!a.hot} onChange={(e) => updateArrayItem("addons", i, "hot", e.target.checked)} /> Evidenzia prezzo</label>
          </Card>
        ))}
      </div>

      {/* NOTES */}
      <h3 className="font-display font-bold text-lg text-ink mb-3">Note in fondo</h3>
      <div className="grid md:grid-cols-3 gap-4">
        {(data.notes || []).map((n, i) => (
          <Card key={i}>
            <div><span className={labelCls}>Titolo</span><input className={inputCls} value={n.t} onChange={(e) => updateArrayItem("notes", i, "t", e.target.value)} /></div>
            <div><span className={labelCls}>Testo</span><textarea rows={2} className={inputCls} value={n.d} onChange={(e) => updateArrayItem("notes", i, "d", e.target.value)} /></div>
          </Card>
        ))}
      </div>
    </div>
  );
}
